import { supabase } from "@/integrations/supabase/client";
import { DailyProgress } from "./gameProgress";
import { hashSolution } from "./solutionHash";

export interface CloudProgress extends DailyProgress {
  puzzle_date: string;
}

export const syncProgressToCloud = async (
  userId: string,
  date: Date,
  progress: DailyProgress
): Promise<void> => {
  const dateStr = date.toISOString().split('T')[0];
  
  // Hash the solution before storing
  const solutionHash = progress.solution ? await hashSolution(progress.solution) : null;
  
  const { error } = await supabase
    .from('game_progress')
    .upsert({
      user_id: userId,
      puzzle_date: dateStr,
      completed: progress.completed,
      won: progress.won,
      attempts: progress.attempts,
      hints_used: progress.hintsUsed,
      guesses: progress.guesses,
      solution: null, // No longer store plaintext
      solution_hash: solutionHash,
      revealed_positions: progress.revealedPositions || {},
      completed_at: progress.completedAt ? new Date(progress.completedAt).toISOString() : null
    }, {
      onConflict: 'user_id,puzzle_date'
    });

  if (error) {
    console.error('Failed to sync progress to cloud:', error);
    throw error;
  }
};

export const loadProgressFromCloud = async (
  userId: string,
  date: Date
): Promise<DailyProgress | null> => {
  const dateStr = date.toISOString().split('T')[0];
  
  const { data, error } = await supabase
    .from('game_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('puzzle_date', dateStr)
    .maybeSingle();

  if (error) {
    console.error('Failed to load progress from cloud:', error);
    return null;
  }

  if (!data) return null;

  return {
    date: dateStr,
    completed: data.completed,
    won: data.won,
    attempts: data.attempts,
    hintsUsed: data.hints_used,
    guesses: data.guesses,
    solution: data.solution, // May be null now
    completedAt: data.completed_at ? new Date(data.completed_at).getTime() : undefined,
    revealedPositions: (data.revealed_positions as Record<number, string>) || {}
  };
};

// Record game result using secure SECURITY DEFINER function
export const recordGameResult = async (
  userId: string,
  date: Date,
  progress: DailyProgress
): Promise<void> => {
  const dateStr = date.toISOString().split('T')[0];
  
  if (!progress.completed) {
    // Only record completed games
    return;
  }

  // Hash the solution before sending to server
  const solutionHash = progress.solution ? await hashSolution(progress.solution) : null;

  const { error } = await supabase.rpc('record_game_result', {
    p_puzzle_date: dateStr,
    p_won: progress.won,
    p_attempts: progress.attempts,
    p_hints_used: progress.hintsUsed,
    p_guesses: progress.guesses,
    p_solution: null, // No longer send plaintext
    p_revealed_positions: progress.revealedPositions || {},
    p_solution_hash: solutionHash
  });

  if (error) {
    console.error('Failed to record game result:', error);
    throw error;
  }
};

// Legacy function - kept for backwards compatibility but deprecated
export const syncStatsToCloud = async (
  userId: string,
  stats: {
    totalPlayed: number;
    totalWon: number;
    currentStreak: number;
    maxStreak: number;
    guessDistribution: Record<number, number>;
  }
): Promise<void> => {
  console.warn('syncStatsToCloud is deprecated. Use recordGameResult instead.');
  // This function is now a no-op as stats are managed by record_game_result
};

export const loadStatsFromCloud = async (userId: string) => {
  const { data, error } = await supabase
    .from('game_stats')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to load stats from cloud:', error);
    return null;
  }

  if (!data) return null;

  return {
    totalPlayed: data.total_played,
    totalWon: data.total_won,
    currentStreak: data.current_streak,
    maxStreak: data.max_streak,
    guessDistribution: data.guess_distribution as Record<number, number>
  };
};

// Load ALL cloud progress entries for a user (for Archive calendar)
export const loadAllProgressFromCloud = async (
  userId: string
): Promise<Map<string, { completed: boolean; won: boolean; guessCount: number; hintsUsed: number }>> => {
  const { data, error } = await supabase
    .from('game_progress')
    .select('puzzle_date, completed, won, guesses, hints_used')
    .eq('user_id', userId);

  if (error) {
    console.error('Failed to load all progress from cloud:', error);
    return new Map();
  }

  const progressMap = new Map<string, { completed: boolean; won: boolean; guessCount: number; hintsUsed: number }>();
  
  for (const row of data || []) {
    progressMap.set(row.puzzle_date, {
      completed: row.completed,
      won: row.won,
      guessCount: row.guesses?.length || 0,
      hintsUsed: row.hints_used || 0
    });
  }

  return progressMap;
};

export const migrateLocalStorageToCloud = async (userId: string): Promise<void> => {
  // Migrate all progress from localStorage to cloud
  const progressKeys = Object.keys(localStorage).filter(key => 
    key.startsWith('wormble-progress-')
  );

  for (const key of progressKeys) {
    const dateStr = key.replace('wormble-progress-', '');
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const progress = JSON.parse(data) as DailyProgress;
        const date = new Date(dateStr);
        await syncProgressToCloud(userId, date, progress);
      } catch (error) {
        console.error(`Failed to migrate progress for ${dateStr}:`, error);
      }
    }
  }

  // Migrate stats (deprecated but kept for backward compatibility)
  const statsData = localStorage.getItem('wormble-stats');
  if (statsData) {
    try {
      const stats = JSON.parse(statsData);
      await syncStatsToCloud(userId, stats);
    } catch (error) {
      console.error('Failed to migrate stats:', error);
    }
  }
};
